import { CalculateStore } from "../../stores/calculateStore";
import { Risk, RiskModel, RisksId } from "../../helpers/risk";
import { ClientStore } from "../../stores/clientStore";
import { RequestMapper } from "../../helpers/requestMapper";
import { ClientLegalGroup } from "../../helpers/client";
import { OwnershipTypeId } from "../../helpers/ownership";
import { PropertyTypeId } from "../../helpers/propertyType";
import { TypeOfActivityId } from "../../helpers/additionalQuestions/typeOfActivity";
import { BusinessActivityId } from "../../helpers/additionalQuestions/businessActivity";
import { RiskModeId } from "../../helpers/risk";
import {PolicyStore} from "../../stores/policyStore";
import {UiFlagsStore} from "../../stores/uiFlagsStore";

export interface RiskNetworkClient {
  legalGroup?: ClientLegalGroup;
  pesel?: string;
  insured?: boolean;
}

export interface RiskNetworkEstate {
  propertyType?: PropertyTypeId;
  ownershipType: OwnershipTypeId;
  partOfFarm?: boolean;
  businessActivity?: BusinessActivityId;
  businessActivityType?: TypeOfActivityId;
  county: string;
}

export interface RiskNetworkInsurance {
  riskMode: RiskModeId;
  detachedHouseOrFlat: boolean;
  onlyFixedElements: boolean;
  movables: boolean;
  nnw: boolean;
  ocInPrivateLife: boolean;
  isPolicyRenewal: boolean;
  flood: boolean | null;
}

export interface RiskNetworkApk {
  businessActivityInGarageOrFarmBuilding?: boolean;
}

export interface RiskNetworkRequest {
  clients: RiskNetworkClient[];
  estate?: RiskNetworkEstate;
  insurance: RiskNetworkInsurance;
  apk?: RiskNetworkApk;
  requestSource: string;
}

export interface RisksNetworkRequestMapper {
  prepareRisksNetworkRequest: (risks: Map<RisksId, RiskModel>) => RiskNetworkRequest;
}

export class DefaultRisksNetworkRequestMapper implements RisksNetworkRequestMapper {
  constructor(private calculateStore: CalculateStore, private clientStore: ClientStore, private policyStore: PolicyStore, private uiFlagsStore: UiFlagsStore) {
  }

  public prepareRisksNetworkRequest(risks: Map<RisksId, RiskModel>): RiskNetworkRequest {
    const additionalQuestions = this.calculateStore.additionalQuestions;
    const clients = this.mapRisksNetworkClients();
    const estate = {
      ownershipType: this.calculateStore.estate.ownershipType.value,
      propertyType: this.calculateStore.estate.propertyType ?? undefined,
      county: this.calculateStore.estate.address.county.value
    };
    RequestMapper.mapPartOfFarm(estate, additionalQuestions);
    RequestMapper.mapBusinessActivityType(estate, additionalQuestions);
    RequestMapper.mapBusinessActivity(estate, additionalQuestions);

    const detachedHouseOrFlat = this.prepareFireAndRandomEvents(RisksId.detachedHouseOrFlatWithFixedElements, risks);
    const onlyFixedElements = this.prepareFireAndRandomEvents(RisksId.onlyFixedElements, risks);
    const movables = this.prepareFireAndRandomEvents(RisksId.movables, risks);
    const riskMode = Risk.getRiskMode(risks);

    const businessActivityInGarageOrFarmBuilding = this.calculateStore.businessActivityInGarageOrFarmBuilding;
    const apk = {
      businessActivityInGarageOrFarmBuilding
    };

    return {
      clients,
      estate: this.calculateStore.propertyInsuranceOrOcChecked ? estate : undefined,
      insurance: {
        riskMode,
        detachedHouseOrFlat,
        onlyFixedElements,
        movables,
        nnw: this.calculateStore.nnw,
        ocInPrivateLife: this.calculateStore.oc,
        isPolicyRenewal: this.policyStore.isPolicyRenewal,
        flood: this.isRenewalOrOfferEdit()? this.calculateStore.initialFloodState : null
      },
      apk: this.calculateStore.externalApk ? undefined : apk,
      requestSource: this.mapRequestSource()
    };
  }

  private mapRisksNetworkClients(): RiskNetworkClient[] {
    return this.clientStore.clients.map(client => ({
      pesel: client.peselForRequest,
      legalGroup: client.legalGroup,
      insured: client.insured
    }));
  }

  private prepareFireAndRandomEvents(riskId: RisksId, risks: Map<RisksId, RiskModel>): boolean {
    return this.calculateStore.propertyInsurance ? Risk.getRiskInPackageValue(riskId, risks) ?? false : false;
  }

  public isRenewalOrOfferEdit() {
    return this.uiFlagsStore.isOfferEdit || this.policyStore.isPolicyRenewal;
  }

  private isClientClickedRemoteSales(): boolean {
    return this.uiFlagsStore.isClientEndsSelected;
  }

  private mapRequestSource(): string {
    return this.isClientClickedRemoteSales() ? "SPRZEDAZZDALNA" : this.getRequestSourceByBirectOrSobol();
  }
  private getRequestSourceByBirectOrSobol(): string {
    return this.uiFlagsStore.isDirect ? "DIRECT" : "SOBOLv2";
  }
}
